package com.elan.edubridge.data.firebase

import com.elan.edubridge.data.firebase.externals.Auth
import com.elan.edubridge.data.firebase.externals.Firestore
import com.elan.edubridge.data.firebase.externals.Functions
import com.elan.edubridge.data.firebase.externals.QueryConstraint
import com.elan.edubridge.data.firebase.externals.collection
import com.elan.edubridge.data.firebase.externals.collectionGroup
import com.elan.edubridge.data.firebase.externals.doc
import com.elan.edubridge.data.firebase.externals.getDoc
import com.elan.edubridge.data.firebase.externals.getDocs
import com.elan.edubridge.data.firebase.externals.httpsCallable
import com.elan.edubridge.data.firebase.externals.limit as firestoreLimit
import com.elan.edubridge.data.firebase.externals.onSnapshot
import com.elan.edubridge.data.firebase.externals.orderBy
import com.elan.edubridge.data.firebase.externals.query
import com.elan.edubridge.data.firebase.externals.where
import com.elan.edubridge.shared.data.datasource.EduConnectDataSource
import com.elan.edubridge.shared.data.firebase.FirebaseNaming
import com.elan.edubridge.shared.domain.Cancellable
import com.elan.edubridge.shared.domain.model.AcademyApplication
import com.elan.edubridge.shared.domain.model.AcademyApplicationStatus
import com.elan.edubridge.shared.domain.model.AcademyData
import com.elan.edubridge.shared.domain.model.Assignment
import com.elan.edubridge.shared.domain.model.AssignmentSubmission
import com.elan.edubridge.shared.domain.model.AttendanceEvent
import com.elan.edubridge.shared.domain.model.AttendanceStatus
import com.elan.edubridge.shared.domain.model.AttendanceSummary
import com.elan.edubridge.shared.domain.model.AttendanceTimelineEntry
import com.elan.edubridge.shared.domain.model.CalendarEvent
import com.elan.edubridge.shared.domain.model.ClassData
import com.elan.edubridge.shared.domain.model.ClassProgressState
import com.elan.edubridge.shared.domain.model.ContentReport
import com.elan.edubridge.shared.domain.model.ContentReportStatus
import com.elan.edubridge.shared.domain.model.DailyRosterEntry
import com.elan.edubridge.shared.domain.model.EventScope
import com.elan.edubridge.shared.domain.model.GuardianLink
import com.elan.edubridge.shared.domain.model.JoinApplication
import com.elan.edubridge.shared.domain.model.JoinApplicationStatus
import com.elan.edubridge.shared.domain.model.NoticeData
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.ReadResult
import com.elan.edubridge.shared.domain.model.PostComment
import com.elan.edubridge.shared.domain.model.PostData
import com.elan.edubridge.shared.domain.model.StudentData
import com.elan.edubridge.shared.domain.model.TodayClass
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.await
import kotlinx.coroutines.channels.awaitClose
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.callbackFlow
import kotlinx.coroutines.launch
import kotlin.js.json

/**
 * [EduConnectDataSource]의 Firestore(named DB `edubridge`) 구현.
 *
 * **실패 규약(인터페이스 KDoc 그대로 적용)**: 복구 가능한 실패(권한 없음·네트워크)는 예외를 던지지
 * 않고 빈 리스트/0값 구조체를 반환한다.
 *
 * **배정 반 스코프(S8 배포 반영, `firebase/firestore.rules` 실측)**: `attendanceRecords`·
 * `attendanceEvents`는 `canReadAttendanceOf(academyId, classId)` — ADMIN은 학원 전체를,
 * EDITOR는 `assignedClassIds`에 포함된 반만 읽을 수 있다. `list` 조회는 반환 문서 전부가 규칙을
 * 통과해야 하므로 클라이언트가 반드시 쿼리를 이 범위로 제약해야 한다([attendanceScopeOrNull]).
 * 배정이 막 바뀐 직후 캐시된 `assignedClassIds`로 쿼리하면 `permission-denied`가 나는데, 이는
 * 오류가 아니라 "멤버십을 다시 읽어라"라는 신호다(§9.3-2) — [dailyRoster]는 그 경우 멤버십을
 * 재조회해 한 번 더 시도한다([queryDailyRoster]).
 *
 * **함정(enrollments, S8 확인)**: `enrollments`는 여전히 스태프 전용이다(`isStaff(academyId)`).
 * `students`는 이제 본인(`userId == uid`)·ACTIVE 보호자도 읽을 수 있게 넓어졌지만 그 다음 단계인
 * `enrollments` 조회가 막혀 있어 [studentSchedule]/[studentAssignments]는 학생·학부모 호출에서는
 * 여전히 빈 목록으로 귀결된다 — 의도적으로 재시도하지 않는다(재시도해도 통과할 방법이 없다).
 * 회원 뷰 화면은 이 두 메서드 대신 [myTimeline]·`memberships`·[classes]로 데이터를 구성해야 한다.
 *
 * **함정(Q18 미해결)**: [todayClasses]의 진행 상태 판정 기준은 계획 §13 Q18이 아직 열려 있다.
 * 여기서는 오늘 요일에 해당하는 수업만 추려 `SCHEDULED`로 고정 반환한다 — 실시간 진행 상태 계산은
 * 이후(Q18 확정 후) 붙인다.
 */
// jsTypeOf/stringOrNull은 Dto.kt의 internal 함수를 그대로 쓴다(같은 패키지, import 불필요).
class FirebaseEduConnectDataSource(
    private val auth: Auth,
    private val firestore: Firestore,
    /** 본인 수강 반 조회는 콜러블뿐이다([enrolledClassIdsFor]) — 그래서 읽기 소스가 Functions를 든다. */
    private val functions: Functions,
) : EduConnectDataSource {

    /**
     * 구독 하나당 Job 하나. 구독마다 스코프를 새로 만들면 부모 없는 루트 스코프가 N개 생기고
     * 아무것도 그것들을 끊지 못한다 — 하나로 묶어 두면 [Cancellable]이 자기 Job만 끊고,
     * `SupervisorJob`이 형제 구독을 서로에게서 실제로 격리한다.
     */
    private val subscriptionScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

    /**
     * 실패를 삼키지 않는 읽기 공통부(#30 단계 2).
     *
     * **이 어댑터에서 쿼리 본문은 `*Result` 쪽에만 있고 구 메서드가 `.items`로 위임한다.** 방향이
     * 반대였다면 같은 쿼리가 두 벌이 되고, 단계 4에서 구 메서드를 지울 때 전부 옮겨 적어야 한다.
     *
     * 코루틴 취소는 통과시킨다 — 계약이 유일하게 허용하는 예외이고, 삼키면 화면 전환 중 취소된
     * 조회가 "불러오지 못했어요"로 잘못 렌더된다.
     */
    private suspend fun <T> readResult(
        operation: String,
        query: suspend () -> List<T>,
    ): ReadResult<T> = try {
        ReadResult.of(query())
    } catch (e: CancellationException) {
        throw e
    } catch (e: Throwable) {
        console.error("[EduBridge] read.$operation failed", e)
        ReadResult(failure = httpsErrorCodeToCommandFailure(e.jsErrorCode()))
    }

    override suspend fun academies(academyIds: List<String>): List<AcademyData> =
        academiesResult(academyIds).items

    /**
     * 학원별 단건 조회의 합이라 **부분 실패가 실재하는 유일한 읽기다**. 하나가 실패해도 나머지는
     * 그리되([ReadResult.isPartialFailure]) 그 사실을 잃지 않는다 — 이전에는 조용히 건너뛰어
     * 학원 하나가 목록에서 사라진 것과 애초에 없는 것이 같아 보였다.
     */
    override suspend fun academiesResult(academyIds: List<String>): ReadResult<AcademyData> {
        val result = mutableListOf<AcademyData>()
        var failure: CommandFailure? = null
        for (id in academyIds) {
            try {
                val snapshot = getDoc(doc(firestore, FirebaseNaming.ACADEMIES, id)).await()
                if (snapshot.exists()) result.add(toAcademyData(snapshot.data(), id))
            } catch (e: CancellationException) {
                throw e
            } catch (e: Throwable) {
                console.error("[EduBridge] read.academy failed", e)
                // 첫 실패만 기억한다 — 화면은 "일부를 못 불러왔다"만 말하고 건별로 나열하지 않는다.
                if (failure == null) failure = httpsErrorCodeToCommandFailure(e.jsErrorCode())
            }
        }
        return ReadResult(items = result, failure = failure)
    }

    override suspend fun classes(academyId: String): List<ClassData> = classesResult(academyId).items

    override suspend fun classesResult(academyId: String): ReadResult<ClassData> =
        readResult("classes") {
            getDocs(
                collection(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.CLASSES),
            ).await().docs.map { toClassData(it.data(), it.id, academyId) }
        }

    override suspend fun students(academyId: String): List<StudentData> =
        studentsResult(academyId).items

    override suspend fun studentsResult(academyId: String): ReadResult<StudentData> =
        readResult("students") {
            getDocs(
                collection(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.STUDENTS),
            ).await().docs.map { toStudentData(it.data(), it.id, academyId) }
        }

    override suspend fun notices(academyId: String): List<NoticeData> = noticesResult(academyId).items

    override suspend fun noticesResult(academyId: String): ReadResult<NoticeData> =
        readResult("notices") {
            getDocs(
                collection(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.NOTICES),
            ).await().docs.map { toNoticeData(it.data(), it.id, academyId) }
        }

    /**
     * 게시판은 학원당 하나라 `notices`와 같은 모양의 단일 컬렉션 조회다.
     *
     * `status == ACTIVE` 서버 필터도 `orderBy`도 걸지 않는다 — 둘을 함께 걸면 복합 인덱스가
     * 필요해지고, 툼스톤 제외·정렬은 `EduConnectRepositoryImpl`이 이미 책임진다.
     */
    override suspend fun posts(academyId: String): List<PostData> = postsResult(academyId).items

    override suspend fun postsResult(academyId: String): ReadResult<PostData> =
        readResult("posts") {
            getDocs(
                collection(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.POSTS),
            ).await().docs.map { toPostData(it.data(), it.id, academyId) }
        }

    override suspend fun post(academyId: String, postId: String): PostData? =
        postResult(academyId, postId).item

    /** 문서 없음은 빈 성공, 읽기 실패는 [ReadResult.failure] — [post]의 null은 이 둘을 뭉갠다. */
    override suspend fun postResult(academyId: String, postId: String): ReadResult<PostData> =
        readResult("post") {
            val snapshot = getDoc(
                doc(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.POSTS, postId),
            ).await()
            if (snapshot.exists()) {
                listOf(toPostData(snapshot.data(), snapshot.id, academyId))
            } else {
                emptyList()
            }
        }

    override suspend fun postComments(academyId: String, postId: String): List<PostComment> =
        postCommentsResult(academyId, postId).items

    override suspend fun postCommentsResult(
        academyId: String,
        postId: String,
    ): ReadResult<PostComment> = readResult("postComments") {
        getDocs(
            collection(
                firestore,
                FirebaseNaming.ACADEMIES,
                academyId,
                AcademySubcollections.POSTS,
                postId,
                PostSubcollections.COMMENTS,
            ),
        ).await().docs.map { toPostComment(it.data(), it.id, academyId, postId) }
    }

    /**
     * 학원 캘린더의 한 구간.
     *
     * **인덱스**: `startAt` 한 필드에만 범위 조건이 걸리므로 Firestore 자동 단일 필드 인덱스로
     * 충분하다 — 복합 인덱스는 필요 없다(같은 이유로 `attendanceRecords`에도 `serviceDate`
     * 단독 인덱스가 없다, `firebase/firestore.indexes.json`). `status == ACTIVE`를 여기 덧붙이면
     * 그 순간 (status, startAt) 복합 인덱스가 필요해지고, 에뮬레이터는 인덱스를 강제하지 않아
     * 프로덕션에서만 빈 목록으로 죽는다 — 툼스톤 제외는 `EduConnectRepositoryImpl`의 몫이다.
     */
    override suspend fun academyEvents(
        academyId: String,
        startAt: String,
        endAt: String,
    ): List<CalendarEvent> = try {
        val base = collection(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.EVENTS)
        val snapshot = getDocs(
            query(base, where("startAt", ">=", startAt), where("startAt", "<=", endAt)),
        ).await()
        snapshot.docs.map { toCalendarEvent(it.data(), it.id, EventScope.ACADEMY, academyId) }
    } catch (e: Throwable) {
        console.error("[EduBridge] read.academyEvents failed", e)
        emptyList()
    }

    /** 개인 캘린더. 경로가 곧 접근 범위라 [academyEvents]와 달리 멤버십을 보지 않는다. */
    override suspend fun personalEvents(
        userId: String,
        startAt: String,
        endAt: String,
    ): List<CalendarEvent> = try {
        val base = collection(firestore, FirebaseNaming.USERS, userId, UserSubcollections.EVENTS)
        val snapshot = getDocs(
            query(base, where("startAt", ">=", startAt), where("startAt", "<=", endAt)),
        ).await()
        snapshot.docs.map { toCalendarEvent(it.data(), it.id, EventScope.PERSONAL, null) }
    } catch (e: Throwable) {
        console.error("[EduBridge] read.personalEvents failed", e)
        emptyList()
    }

    /** 학원 단위 과제 목록. `notices`/`classes`와 같은 패턴 — ACTIVE 멤버 전원이 읽는다(rules). */
    override suspend fun assignments(academyId: String): List<Assignment> =
        assignmentsResult(academyId).items

    override suspend fun assignmentsResult(academyId: String): ReadResult<Assignment> =
        readResult("assignments") {
            val snapshot = getDocs(
                collection(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.ASSIGNMENTS),
            ).await()
            snapshot.docs.map { toAssignment(it.data(), it.id, academyId) }
        }

    override suspend fun assignmentSubmissions(
        academyId: String,
        assignmentId: String,
    ): List<AssignmentSubmission> = assignmentSubmissionsResult(academyId, assignmentId).items

    /** 한 과제의 제출은 그 반·학원 학생 수만큼이라 필터 없이 통째로 읽는다(rules가 스태프만 연다). */
    override suspend fun assignmentSubmissionsResult(
        academyId: String,
        assignmentId: String,
    ): ReadResult<AssignmentSubmission> = readResult("assignmentSubmissions") {
        val snapshot = getDocs(
            collection(
                firestore,
                FirebaseNaming.ACADEMIES,
                academyId,
                AcademySubcollections.ASSIGNMENTS,
                assignmentId,
                AcademySubcollections.SUBMISSIONS,
            ),
        ).await()
        snapshot.docs.map { toAssignmentSubmission(it.data(), it.id, academyId, assignmentId) }
    }

    override suspend fun studentSchedule(academyId: String, studentUserId: String): List<ClassData> =
        studentScheduleResult(academyId, studentUserId).items

    /**
     * 수강 반 id 조회 + 반 목록 조회의 2단이다. 두 번째 단을 [classes]로 부르면 그쪽이 실패를
     * 삼켜 시간표가 조용히 비고 "수업 없음"으로 보인다 — [classesResult]로 실패를 물려받는다.
     */
    override suspend fun studentScheduleResult(
        academyId: String,
        studentUserId: String,
    ): ReadResult<ClassData> {
        val enrolled = readResult("studentSchedule") { enrolledClassIdsFor(academyId, studentUserId) }
        if (!enrolled.isSuccess) return ReadResult(failure = enrolled.failure)
        if (enrolled.items.isEmpty()) return ReadResult()
        val all = classesResult(academyId)
        return ReadResult(
            items = all.items.filter { it.id in enrolled.items },
            failure = all.failure,
        )
    }

    override suspend fun studentAssignments(academyId: String, studentUserId: String): List<Assignment> =
        studentAssignmentsResult(academyId, studentUserId).items

    override suspend fun studentAssignmentsResult(
        academyId: String,
        studentUserId: String,
    ): ReadResult<Assignment> = readResult("studentAssignments") {
        val base = collection(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.ASSIGNMENTS)
        // 반을 지정하지 않은 과제(classId == null)는 학원 전체에 낸 것이다. Firestore는
        // `in`과 `== null`을 한 쿼리로 OR 할 수 없으므로 두 번 읽고 합친다 — 반 필터만 걸면
        // 학원 전체 과제가 학생에게 영영 보이지 않는다.
        val academyWide = getDocs(query(base, where("classId", "==", null)))
            .await().docs.map { toAssignment(it.data(), it.id, academyId) }
        val enrolledClassIds = enrolledClassIdsFor(academyId, studentUserId)
        val classScoped = if (enrolledClassIds.isEmpty()) {
            emptyList()
        } else {
            // Firestore `in`은 최대 30개까지만 허용한다 — 이 앱 규모에서 한 학생의 수강 반 수는
            // 이를 넘지 않는다고 가정한다.
            getDocs(
                query(base, where("classId", "in", enrolledClassIds.take30Warned("studentAssignments.classId").toTypedArray())),
            ).await().docs.map { toAssignment(it.data(), it.id, academyId) }
        }
        (academyWide + classScoped).distinctBy { it.id }
    }

    override suspend fun todayClasses(academyId: String, serviceDate: String?): List<TodayClass> {
        val resolvedDate = serviceDate ?: todayServiceDate()
        val weekday = isoDayOfWeek(resolvedDate)
        return classes(academyId)
            .filter { weekday in it.schedule.daysOfWeek }
            .map { classData ->
                TodayClass(
                    classId = classData.id,
                    academyId = classData.academyId,
                    className = classData.className,
                    subject = classData.subject,
                    teacher = classData.teacher,
                    schedule = classData.schedule,
                    enrolledCount = classData.studentCount,
                    checkedInCount = 0,
                    state = ClassProgressState.SCHEDULED,
                )
            }
    }

    override suspend fun attendanceSummary(academyId: String, serviceDate: String?): AttendanceSummary {
        val resolvedDate = serviceDate ?: todayServiceDate()
        // dailyRoster()를 그대로 재사용한다 — 배정 반 스코프·재시도 로직이 자동으로 상속된다.
        val roster = dailyRoster(academyId, resolvedDate, null)
        return AttendanceSummary(
            academyId = academyId,
            serviceDate = resolvedDate,
            classCount = roster.map { it.classId }.distinct().size,
            checkedInCount = roster.count {
                it.status == AttendanceStatus.PRESENT || it.status == AttendanceStatus.LATE
            },
            checkedOutCount = roster.count { it.status == AttendanceStatus.LEFT },
            notArrivedCount = roster.count { it.status == AttendanceStatus.NOT_ARRIVED },
        )
    }

    override suspend fun dailyRoster(
        academyId: String,
        serviceDate: String?,
        classId: String?,
    ): List<DailyRosterEntry> = queryDailyRoster(academyId, serviceDate ?: todayServiceDate(), classId, isRetry = false)

    /**
     * `attendanceRecords` 조회 + 배정 반 스코프 + `permission-denied` 1회 재시도.
     *
     * 매 호출(재시도 포함)마다 [currentMembership]을 새로 읽으므로, 재귀 호출 자체가 "멤버십을
     * 다시 읽어 최신 `assignedClassIds`로 다시 시도"를 자연스럽게 구현한다. `isRetry`가 이미 true인
     * 상태에서 또 실패하면 더 재시도하지 않고 빈 목록으로 흡수한다(최대 2회 시도).
     */
    private suspend fun queryDailyRoster(
        academyId: String,
        serviceDate: String,
        classId: String?,
        isRetry: Boolean,
    ): List<DailyRosterEntry> {
        val scope = attendanceScopeOrNull(academyId, classId) ?: return emptyList()
        return try {
            val base = collection(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.ATTENDANCE_RECORDS)
            val constraints = mutableListOf(where("serviceDate", "==", serviceDate))
            constraints += scope
            val snapshot = getDocs(query(base, *constraints.toTypedArray())).await()
            snapshot.docs.map { toDailyRosterEntry(it.data(), academyId) }
        } catch (e: Throwable) {
            console.error("[EduBridge] read.dailyRoster failed", e)
            if (!isRetry && e.jsErrorCode()?.substringAfterLast('/') == "permission-denied") {
                queryDailyRoster(academyId, serviceDate, classId, isRetry = true)
            } else {
                emptyList()
            }
        }
    }

    override suspend fun attendanceRecordsRange(
        academyId: String,
        startDate: String,
        endDate: String,
        classId: String?,
        studentId: String?,
    ): List<DailyRosterEntry> =
        queryAttendanceRecordsRange(academyId, startDate, endDate, classId, studentId, isRetry = false)

    /**
     * [queryDailyRoster]의 기간 확장판 — 배정 반 스코프도 `permission-denied` 1회 재시도도 같다.
     * `serviceDate`가 `yyyy-MM-dd`로 정규화돼 있어 문자열 부등호가 곧 날짜 구간이다(양끝 포함, R12).
     */
    private suspend fun queryAttendanceRecordsRange(
        academyId: String,
        startDate: String,
        endDate: String,
        classId: String?,
        studentId: String?,
        isRetry: Boolean,
    ): List<DailyRosterEntry> {
        val scope = attendanceScopeOrNull(academyId, classId) ?: return emptyList()
        return try {
            val base = collection(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.ATTENDANCE_RECORDS)
            val constraints = mutableListOf(
                where("serviceDate", ">=", startDate),
                where("serviceDate", "<=", endDate),
            )
            if (studentId != null) constraints += where("studentId", "==", studentId)
            constraints += scope
            val snapshot = getDocs(query(base, *constraints.toTypedArray())).await()
            snapshot.docs.map { toDailyRosterEntry(it.data(), academyId) }
        } catch (e: Throwable) {
            console.error("[EduBridge] read.attendanceRecordsRange failed", e)
            if (!isRetry && e.jsErrorCode()?.substringAfterLast('/') == "permission-denied") {
                queryAttendanceRecordsRange(academyId, startDate, endDate, classId, studentId, isRetry = true)
            } else {
                emptyList()
            }
        }
    }

    override suspend fun myTimeline(
        userId: String,
        academyId: String?,
        subjectStudentId: String?,
        limit: Int,
    ): List<AttendanceTimelineEntry> = try {
        val base = collection(firestore, FirebaseNaming.USERS, userId, UserSubcollections.ATTENDANCE_TIMELINE)
        val constraints = mutableListOf<QueryConstraint>()
        if (academyId != null) constraints += where("academyId", "==", academyId)
        if (subjectStudentId != null) constraints += where("subjectStudentId", "==", subjectStudentId)
        constraints += orderBy("occurredAt", "desc")
        constraints += firestoreLimit(limit)
        val snapshot = getDocs(query(base, *constraints.toTypedArray())).await()
        snapshot.docs.map { toAttendanceTimelineEntry(it.data(), it.id) }
    } catch (e: Throwable) {
        // 개인정보 미러 조회 실패는 조용히 빈 목록으로 흡수하되(§4.3 실패 규약), 원인 파악은 남겨 둔다 —
        // 권한 문제(멤버십 미갱신)인지 진짜 장애인지 콘솔 없이는 구분할 수 없었다.
        console.error("[EduBridge] read.myTimeline failed", e)
        emptyList()
    }

    override suspend fun guardianLinks(guardianUserId: String): List<GuardianLink> = try {
        // guardianLinks는 academies/{academyId}/guardianLinks 하위라 academyId 없이 조회하려면
        // collectionGroup이 필요하다(단일 collection() 경로로는 표현 불가).
        val snapshot = getDocs(
            query(
                collectionGroup(firestore, AcademySubcollections.GUARDIAN_LINKS),
                where("guardianUserId", "==", guardianUserId),
            ),
        ).await()
        snapshot.docs.map { toGuardianLink(it.data()) }
    } catch (e: Throwable) {
        console.error("[EduBridge] read.guardianLinks failed", e)
        emptyList()
    }

    /**
     * 한 학생에게 연결된 보호자 전원(REVOKED 포함, 해지 이력 표시용). [guardianLinks]와 달리
     * academyId가 이미 있어 collectionGroup 없이 단일 컬렉션 쿼리로 끝난다. 스태프 전용 경로다
     * (rules `isStaff(academyId)` — 당사자 본인도 각자 필드 일치로 통과하지만 이 메서드는
     * "학생 상세 화면"이라는 스태프 사용처를 전제한다).
     */
    override suspend fun studentGuardianLinks(academyId: String, studentId: String): List<GuardianLink> = try {
        val base = collection(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.GUARDIAN_LINKS)
        val snapshot = getDocs(query(base, where("studentId", "==", studentId))).await()
        snapshot.docs.map { toGuardianLink(it.data()) }
    } catch (e: Throwable) {
        console.error("[EduBridge] read.studentGuardianLinks failed", e)
        emptyList()
    }

    /**
     * SUPER_ADMIN 승인 큐. rules(`edubridge_academyApplications`)는 SUPER_ADMIN 또는 신청자
     * 본인에게만 read를 연다 — SUPER_ADMIN이 아닌 caller가 전체 PENDING 목록을 쿼리하면 자기 신청이
     * 아닌 문서마다 규칙을 통과하지 못해 쿼리 전체가 거부되고, 그대로 빈 목록으로 흡수된다(의도된
     * 동작 — 별도 권한 분기가 필요 없다).
     */
    override suspend fun pendingAcademyApplications(): List<AcademyApplication> =
        pendingAcademyApplicationsResult().items

    override suspend fun pendingAcademyApplicationsResult(): ReadResult<AcademyApplication> =
        readResult("pendingAcademyApplications") {
            val base = collection(firestore, TopLevelCollections.ACADEMY_APPLICATIONS)
            getDocs(query(base, where("status", "==", AcademyApplicationStatus.PENDING.name)))
                .await().docs.map { toAcademyApplication(it.data(), it.id) }
        }

    /**
     * rules가 문서마다 `applicantUid == uid`를 요구하므로 `applicantUid`로 쿼리를 제약해야 한다 —
     * [pendingAcademyApplications]처럼 `status`만 걸면 SUPER_ADMIN이 아닌 호출자는 쿼리 전체가
     * 거부된다. 정렬은 서버 `orderBy`를 붙이면 인덱스가 하나 더 필요해져 클라이언트에서 한다.
     */
    override suspend fun myAcademyApplications(applicantUid: String): List<AcademyApplication> =
        myAcademyApplicationsResult(applicantUid).items

    override suspend fun myAcademyApplicationsResult(
        applicantUid: String,
    ): ReadResult<AcademyApplication> = readResult("myAcademyApplications") {
        val base = collection(firestore, TopLevelCollections.ACADEMY_APPLICATIONS)
        getDocs(query(base, where("applicantUid", "==", applicantUid))).await()
            .docs.map { toAcademyApplication(it.data(), it.id) }
            .sortedByDescending { it.createdAt }
    }

    override suspend fun contentReports(academyId: String, openOnly: Boolean): List<ContentReport> =
        contentReportsResult(academyId, openOnly).items

    override suspend fun contentReportsResult(
        academyId: String,
        openOnly: Boolean,
    ): ReadResult<ContentReport> = readResult("contentReports") {
        val base = collection(
            firestore,
            FirebaseNaming.ACADEMIES,
            academyId,
            AcademySubcollections.CONTENT_REPORTS,
        )
        val source = if (openOnly) {
            query(
                base,
                where("status", "==", ContentReportStatus.OPEN.name),
                orderBy("createdAt", "desc"),
            )
        } else {
            query(base, orderBy("createdAt", "desc"))
        }
        getDocs(source).await().docs.map { toContentReport(it.data(), it.id, academyId) }
    }

    /**
     * W7 스태프 가입 신청 큐. [contentReports]와 동형의 직접 읽기이며, 인덱스가
     * `(status, submittedAt desc)`로 이미 배포돼 있어(`firestore.indexes.json`) 상태 필터와
     * 서버 정렬을 함께 건다 — 대기 큐는 오래 기다린 신청이 아니라 **최근 제출이 위**다
     * (재신청이 `submittedAt`을 갱신하므로 이 순서가 "방금 움직인 신청"을 보여 준다).
     */
    override suspend fun joinApplications(
        academyId: String,
        status: JoinApplicationStatus,
    ): List<JoinApplication> = joinApplicationsResult(academyId, status).items

    override suspend fun joinApplicationsResult(
        academyId: String,
        status: JoinApplicationStatus,
    ): ReadResult<JoinApplication> = readResult("joinApplications") {
        val base = collection(
            firestore,
            FirebaseNaming.ACADEMIES,
            academyId,
            AcademySubcollections.JOIN_APPLICATIONS,
        )
        val source = query(
            base,
            where("status", "==", status.name),
            orderBy("submittedAt", "desc"),
        )
        getDocs(source).await().docs.map { toJoinApplication(it.data(), academyId, it.id) }
    }

    /**
     * W7 본인 미러. 문서가 학원 수만큼이라 필터 없이 전부 읽고, 정렬은
     * [myAcademyApplications]와 같은 이유(인덱스 추가 회피)로 클라이언트에서 한다.
     */
    override suspend fun myJoinApplications(userId: String): List<JoinApplication> =
        myJoinApplicationsResult(userId).items

    override suspend fun myJoinApplicationsResult(userId: String): ReadResult<JoinApplication> =
        readResult("myJoinApplications") {
            val base = collection(
                firestore,
                FirebaseNaming.USERS,
                userId,
                UserSubcollections.JOIN_APPLICATIONS,
            )
            getDocs(base).await().docs
                .map { toJoinApplication(it.data(), it.id, userId) }
                .sortedByDescending { it.submittedAt }
        }

    /**
     * 포트가 콜백인 것은 Swift 때문이다 — `Flow`는 ObjC 헤더로 브리징되지 않아 Swift가
     * `EduConnectDataSource`를 구현할 수 없다. 웹은 아래 `callbackFlow` 본문을 그대로 쓰고
     * 여기서 얇게 감싸기만 한다. 스코프 계산([attendanceScopeOrNull])이 `suspend`라 구독 자체가
     * 코루틴을 필요로 하므로, 리스너 등록 경합을 손으로 다시 짜는 것보다 안전하다.
     *
     * [Cancellable]은 이 구독의 Job만 끊고, `awaitClose`가 `unsubscribe()`까지 이어 준다.
     */
    override fun observeAttendanceEvents(
        academyId: String,
        limit: Int,
        onEach: (List<AttendanceEvent>) -> Unit,
        onComplete: () -> Unit,
    ): Cancellable {
        val job = subscriptionScope.launch {
            try {
                attendanceEventFlow(academyId, limit).collect { onEach(it) }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Throwable) {
                // 실패는 예외가 아니라 종료로 표현한다. 여기서 새어 나가면 onComplete을 건너뛰어
                // 바깥 collector가 영원히 매달린다 — 화면은 오류도 없이 멈춘 피드를 계속 보여 준다.
                console.error("[EduBridge] read.observeAttendanceEvents failed", e)
            }
            // onComplete도 경계 콜백이라 같은 가드를 받는다. try 밖에 두는 것 자체는 의도다:
            // 수집이 실패로 끝나도 종료는 알려야 한다.
            try {
                onComplete()
            } catch (e: CancellationException) {
                throw e
            } catch (e: Throwable) {
                console.error("[EduBridge] read.observeAttendanceEvents.onComplete failed", e)
            }
        }
        return Cancellable { job.cancel() }
    }

    /**
     * 실시간 출결 피드. 구독 시작 시점의 멤버십으로 배정 반 스코프를 한 번 계산해 쿼리를 세운다.
     *
     * `suspend`가 아니라서 [dailyRoster]처럼 실패 시점에 멤버십을 재조회해 재시도할 자연스러운
     * 지점이 없다 — 구독 도중 배정이 바뀌어 스트림이 끊기면(onError) 조용히 닫는다. 화면은
     * 스트림이 `permission-denied`로 끝나면 새 스코프로 다시 구독해야 한다.
     */
    private fun attendanceEventFlow(academyId: String, limit: Int): Flow<List<AttendanceEvent>> = callbackFlow {
        val scope = attendanceScopeOrNull(academyId, classId = null)
        if (scope == null) {
            close()
            return@callbackFlow
        }
        val base = collection(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.ATTENDANCE_EVENTS)
        val constraints = mutableListOf<QueryConstraint>()
        constraints += scope
        constraints += orderBy("occurredAt", "desc")
        constraints += firestoreLimit(limit)
        val liveQuery = query(base, *constraints.toTypedArray())
        val unsubscribe = onSnapshot(
            liveQuery,
            { snapshot -> trySend(snapshot.docs.map { toAttendanceEvent(it.data(), it.id) }) },
            { _ -> close() },
        )
        awaitClose { unsubscribe() }
    }

    /**
     * 호출자의 `edubridge_academies/{academyId}/members/{uid}`를 읽어 `attendanceRecords`/
     * `attendanceEvents` 쿼리 제약을 만든다(`firebase/firestore.rules`의 `canReadAttendanceOf`와
     * 동일 판정). 읽을 수 있는 범위가 전혀 없으면(비로그인, VIEWER, 또는 배정 반이 없는 EDITOR가
     * 특정 반을 요청하지 않은 경우) null을 돌려주고 — 호출부는 쿼리를 아예 보내지 않는다.
     *
     * ADMIN: 무제약(요청한 [classId]가 있으면 그 반만). EDITOR: `assignedClassIds` 교집합만 —
     * 빈 배열로 Firestore `in`을 호출하면 클라이언트에서 즉시 예외가 나므로 비어 있으면 null.
     * classId가 null인 문서(학원 단위 QR 등하원)는 ADMIN만 읽는다(rules) — EDITOR의 `in` 목록에는
     * 항상 실제 classId 문자열만 들어가므로 별도 처리 없이 자연히 제외된다.
     */
    private suspend fun attendanceScopeOrNull(academyId: String, classId: String?): List<QueryConstraint>? {
        val (permission, assignedClassIds) = currentMembership(academyId)
        return when (permission) {
            Permission.ADMIN -> if (classId != null) listOf(where("classId", "==", classId)) else emptyList()
            Permission.EDITOR -> {
                val scopedIds = if (classId != null) {
                    if (classId in assignedClassIds) listOf(classId) else emptyList()
                } else {
                    assignedClassIds
                }
                if (scopedIds.isEmpty()) null else listOf(where("classId", "in", scopedIds.take30Warned("attendanceScope.classId").toTypedArray()))
            }
            Permission.VIEWER -> null
        }
    }

    /**
     * 호출자 본인의 멤버십을 읽어 (permission, assignedClassIds)를 돌려준다. 비로그인·문서
     * 없음·읽기 실패는 전부 VIEWER + 빈 목록(가장 좁은 권한)으로 취급한다 — 넓혀서 실패하는 것보다
     * 좁혀서 빈 결과를 내는 편이 안전하다.
     */
    private suspend fun currentMembership(academyId: String): Pair<Permission, List<String>> {
        val uid = auth.currentUser?.uid ?: return Permission.VIEWER to emptyList()
        return try {
            val snapshot = getDoc(
                doc(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.MEMBERS, uid),
            ).await()
            if (!snapshot.exists()) return Permission.VIEWER to emptyList()
            val data = snapshot.data()
            val permission = when (stringOrNull(data, "permission")) {
                "ADMIN" -> Permission.ADMIN
                "EDITOR" -> Permission.EDITOR
                else -> Permission.VIEWER
            }
            val assignedClassIds = run {
                val raw = data["assignedClassIds"]
                if (js("!Array.isArray(raw)") as Boolean) {
                    emptyList()
                } else {
                    val array = raw.unsafeCast<Array<dynamic>>()
                    array.mapNotNull { if (jsTypeOf(it) == "string") it as String else null }
                }
            }
            permission to assignedClassIds
        } catch (e: Throwable) {
            console.error("[EduBridge] read.currentMembership failed", e)
            Permission.VIEWER to emptyList()
        }
    }

    /**
     * 학생 계정(uid) -> 학원 내 studentId -> 등록된 classId 목록.
     *
     * **함정(S8 확인)**: `students`는 이제 본인(`userId == uid`)도 읽을 수 있지만, 그다음 단계인
     * `enrollments`는 여전히 `isStaff(academyId)` 전용이다(`firebase/firestore.rules:163-166`).
     * 그래서 학생·학부모 호출은 두 번째 쿼리에서 `permission-denied`로 끝나 항상 빈 목록이 된다 —
     * 의도적으로 재시도하지 않는다(재시도해도 통과할 방법이 없는 구조적 제약이다). 이 메서드는
     * 스태프가 특정 학생의 시간표/과제를 조회하는 경로에서만 실제 데이터를 돌려준다.
     */
    /**
     * 수강 반 id.
     *
     * **본인 것은 콜러블로 받는다.** `enrollments` 는 rules가 스태프에게만 열어 둔 컬렉션이라
     * (다른 학생이 어느 반인지가 거기 다 있다) 학생·보호자가 직접 목록 조회하면 거부되고,
     * 그 실패가 과제·시간표 조회 전체를 삼킨다. 서버가 대신 읽고 id만 돌려준다
     * (`edubridge-listMyClassIds`). 보호자는 ACTIVE 링크로 연결된 자녀의 반도 함께 받는다.
     *
     * 남의 것을 물을 때(스태프 화면)는 예전 그대로 Firestore를 읽는다 — 콜러블은 호출자 기준으로만
     * 답하므로, 그 경로에 콜러블을 쓰면 "다른 학생의 반"을 물었는데 내 반이 돌아온다.
     */
    private suspend fun enrolledClassIdsFor(academyId: String, studentUserId: String): List<String> {
        if (studentUserId.isNotBlank() && studentUserId == auth.currentUser?.uid) {
            val response = httpsCallable(
                functions,
                "${FirebaseNaming.FUNCTIONS_PREFIX}listMyClassIds",
            )(json("academyId" to academyId)).await()
            val classIds = response.data["classIds"] as? Array<*> ?: return emptyList()
            return classIds.filterIsInstance<String>()
        }

        val studentsRef = collection(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.STUDENTS)
        val studentSnapshot = getDocs(query(studentsRef, where("userId", "==", studentUserId))).await()
        val studentIds = studentSnapshot.docs.map { it.id }
        if (studentIds.isEmpty()) return emptyList()

        val enrollmentsRef = collection(firestore, FirebaseNaming.ACADEMIES, academyId, AcademySubcollections.ENROLLMENTS)
        val enrollmentSnapshot = getDocs(
            query(enrollmentsRef, where("studentId", "in", studentIds.take30Warned("enrolledClassIdsFor.studentId").toTypedArray())),
        ).await()
        return enrollmentSnapshot.docs.mapNotNull { stringOrNull(it.data(), "classId") }
    }

    /**
     * Firestore `in` 절 상한(30개) 방어. 이 앱 규모에서는 넘지 않는다고 가정하지만, 실제로 넘으면
     * 초과분이 조용히 잘려 나가 "왜 일부만 조회되지?"를 콘솔 없이는 진단할 수 없다(리뷰 항목).
     */
    private fun <T> List<T>.take30Warned(context: String): List<T> {
        if (size > 30) {
            console.warn("[EduBridge] $context: ${size}건 중 30건만 조회합니다(Firestore 'in' 상한 초과).")
        }
        return take(30)
    }
}

/**
 * UTC 기준 오늘 날짜(`yyyy-MM-dd`). `functions/src/common/validation.ts`의 `serviceDateIn()`과
 * 달리 학원 `timeZone`을 반영하지 못한다 — academyId만으로는 학원 설정을 추가 왕복 없이 알 수
 * 없어 생기는 근사치다(R12 관련, Q18과 같은 미해결 범주).
 */
/**
 * 학원 기준 KST의 오늘(§4-9 결정). 이전 구현은 UTC 그대로였다 —
 * KST 자정~오전 9시 사이에는 "오늘 수업·출결"이 어제 날짜로 조회되는 실버그였다.
 */
private fun todayServiceDate(): String =
    js("new Date(Date.now() + 32400000).toISOString().slice(0, 10)") as String

/**
 * ISO-8601 요일(1=월 … 7=일). `functions/src/common/validation.ts`의 `isoDayOfWeek()`와 같은 규약
 * (UTC 자정 기준 파싱)이다.
 */
private fun isoDayOfWeek(serviceDate: String): Int {
    val jsDay = js("new Date(serviceDate + 'T00:00:00Z').getUTCDay()") as Int // 0=일 ... 6=토
    return if (jsDay == 0) 7 else jsDay
}
